import * as React from 'react'
import cx from 'classnames'
import { useObservableFocus } from 'hooks/focus-hooks'
import { NEVER, Observable } from 'rxjs'
import { ControlStatus } from '../ControlStatus'
import styles from './TextInput.sass'

export interface TextInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  staticText?: string
  status?: ControlStatus
  value?: string
  focus$?: Observable<unknown>

  onFocus?(e: React.FocusEvent<HTMLDivElement>): unknown

  onBlur?(e: React.FocusEvent<HTMLDivElement>): unknown

  // if we want to change this to be the default browser control we can do this too
  // but for now lets keep it separate
  controlType?: 'text' | 'password' | 'color' | 'email' | 'tel'

  /**
   * Optionally adds a control button to the text input field.
   */
  button?: React.ReactNode

  /**
   * Optionally adds an avatar or icon to the text input field.
   * (Adding any other element type that is not an avatar/icon, will mess with the styling)
   */
  visual?: React.ReactNode
}

export default function TextInput({
  visual,
  staticText,
  className,
  value,
  onChange,
  onFocus,
  onBlur,
  focus$ = NEVER,
  controlType = 'text',
  button,
  status = 'default',
  ...props
}: TextInputProps) {
  const [focus, setFocus] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const color = value || ''
  const validColor = /^#[0-9A-F]{6}$/i.test(color)
  const focusInput = React.useCallback(() => inputRef.current?.focus(), [])
  useObservableFocus(focus$, focusInput)
  return (
    <div
      className={cx(
        styles.textInputWrapper,
        props.disabled && styles.disabled,
        controlType === 'color' && styles.colorInput,
        button && styles.withButton,
        styles[status],
        focus && styles.focus,
      )}
      onClick={focusInput}
    >
      {controlType === 'color' && (
        <div
          className={styles.colorHint}
          style={{ backgroundColor: validColor ? color : 'transparent' }}
        />
      )}
      {visual && <div className={styles.withVisual}>{visual}</div>}
      <input
        ref={inputRef}
        type="text"
        className={cx(styles.input, className)}
        {...props}
        onFocus={(e) => {
          setFocus(true)
          onFocus?.(e)
        }}
        onBlur={(e) => {
          setFocus(false)
          onBlur?.(e)
        }}
        value={value}
        onChange={onChange}
      />
      {staticText && <div className={styles.inputStaticText}>{staticText}</div>}
      {button}
    </div>
  )
}
